import { MouseEvent, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import type { ThemeMode } from './Layout';
import { useSectionNav } from './useSectionNav';

interface SiteHeaderProps {
  mode: ThemeMode;
  onToggleMode: () => void;
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

function SiteHeader({ mode, onToggleMode }: SiteHeaderProps) {
  const { t, lang, setLang } = useLanguage();
  const { pathname } = useLocation();
  const goTo = useSectionNav();
  const [menuOpen, setMenuOpen] = useState(false);

  const onSection = (id: string) => (e: MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    goTo(id);
  };

  const onBlog = pathname.startsWith('/blog');
  const onPhotos = pathname.startsWith('/photos');

  const controls = (
    <>
      <div role="group" aria-label="Language" className="ft-seg ft-lang">
        {(['en', 'tr'] as const).map((l) => (
          <button key={l} type="button" aria-pressed={lang === l} className={lang === l ? 'active' : ''} onClick={() => setLang(l)}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <button type="button" className="ft-icon-btn" aria-label={t.nav.themeLabel} onClick={onToggleMode}>
        {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
      </button>
      <a href="/#contact" className="ft-open" onClick={onSection('contact')}>
        <span className="ft-dot" />
        {t.nav.open}
      </a>
    </>
  );

  const sheetItems: { label: string; id?: string; to?: string }[] = [
    { label: t.nav.work, id: 'work' },
    { label: t.nav.clients, id: 'clients' },
    { label: t.nav.about, id: 'about' },
    { label: t.nav.writing, to: '/blog' },
    { label: t.nav.photos, to: '/photos' },
    { label: t.nav.contact, id: 'contact' },
  ];

  return (
    <header className="ft-header">
      <a href="/" className="ft-logo ld" onClick={onSection('top')}>
        <span className="ft-logo-mark">ft</span>
        <span className="ft-logo-name">Furkan Turan</span>
      </a>

      <nav aria-label="Main" className="ft-nav ft-desk ld ld-2">
        <a href="/#work" className={!onBlog && !onPhotos ? 'active' : ''} onClick={onSection('work')}>
          {t.nav.work}
        </a>
        <a href="/#clients" onClick={onSection('clients')}>
          {t.nav.clients}
        </a>
        <a href="/#about" onClick={onSection('about')}>
          {t.nav.about}
        </a>
        <Link to="/blog" className={onBlog ? 'active' : ''}>
          {t.nav.writing}
        </Link>
      </nav>

      <div className="ft-controls ft-desk ld ld-3">{controls}</div>

      <button
        type="button"
        className="ft-icon-btn ft-mob"
        aria-label={t.nav.menu}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <span className={`ft-burger${menuOpen ? ' open' : ''}`}>
          <span />
          <span />
        </span>
      </button>

      {menuOpen && (
        <div className="ft-sheet" role="dialog" aria-label={t.nav.menu}>
          {sheetItems.map((item, i) => {
            const content = (
              <>
                <span>{item.label}</span>
                <span className="ft-sheet-num">{String(i + 1).padStart(2, '0')}</span>
              </>
            );
            const style = { animationDelay: `${0.05 * (i + 1)}s` };
            return item.to ? (
              <Link key={item.label} to={item.to} className="ft-it ft-sheet-link" style={style} onClick={() => setMenuOpen(false)}>
                {content}
              </Link>
            ) : (
              <a key={item.label} href={`/#${item.id}`} className="ft-it ft-sheet-link" style={style} onClick={onSection(item.id!)}>
                {content}
              </a>
            );
          })}
          <div className="ft-it ft-sheet-controls" style={{ animationDelay: `${0.05 * (sheetItems.length + 1)}s` }}>
            {controls}
          </div>
        </div>
      )}
    </header>
  );
}

export default SiteHeader;
